import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, XCircle, RefreshCw, Link2, Copy, Clock, Plus, Trash2 } from "lucide-react";
import BuyerPicker, { BuyerBadge } from "./BuyerPicker";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const HOLDS = [1, 4, 6, 12, 24];
const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-700", paid: "bg-green-100 text-green-700",
  expired: "bg-slate-200 text-slate-600", cancelled: "bg-red-100 text-red-600", failed: "bg-red-100 text-red-600",
};
const newLine = (ticket_type_id = "") => ({ key: `${Date.now()}-${Math.random()}`, ticket_type_id, variant_id: "", quantity: 1, override: false, unit_price: "" });
const input = "w-full px-3 py-2.5 rounded-xl border text-sm";

function capacityOf(tier, lines, i) {
  const left = tier?.seats_left ?? null;
  const others = lines.reduce((n, l, j) => n + (j !== i && l.ticket_type_id === tier?.id ? Number(l.quantity) || 0 : 0), 0);
  return left === null ? { left, max: 20 } : { left, max: Math.max(0, Math.min(20, left - others)) };
}

function LineEditor({ line, i, tiers, lines, quoteLine, onChange, onRemove }) {
  const tier = tiers.find(t => t.id === line.ticket_type_id);
  const { left, max } = capacityOf(tier, lines, i);
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid={`issue-line-${i}`}>
      <div className="grid grid-cols-[1fr_72px_auto] sm:grid-cols-[1fr_1fr_90px_auto] gap-2 items-center">
        <select value={line.ticket_type_id} onChange={(e) => onChange({ ticket_type_id: e.target.value, variant_id: "" })} className={`${input} col-span-3 sm:col-span-1`} data-testid={`issue-tier-select-${i}`}>
          <option value="">Choose ticket…</option>
          {tiers.map(t => <option key={t.id} value={t.id}>{`${t.name}`}</option>)}
        </select>
        {tier?.variants?.length > 0 ? (
          <select value={line.variant_id} onChange={(e) => onChange({ variant_id: e.target.value })} className={`${input} col-span-1`} data-testid={`issue-variant-select-${i}`}>
            <option value="">Choose variant…</option>
            {tier.variants.map(v => <option key={v.id} value={v.id}>{`${v.name}`}</option>)}
          </select>
        ) : <div />}
        <input type="number" min="1" max={max} value={line.quantity} onChange={(e) => onChange({ quantity: e.target.value })} className={input} aria-label="Quantity" data-testid={`issue-qty-input-${i}`} />
        {lines.length > 1 ? <button onClick={onRemove} className="p-2 text-red-500 justify-self-end" aria-label="Remove line" data-testid={`issue-line-remove-${i}`}><Trash2 className="w-4 h-4" /></button> : <span className="w-8" />}
      </div>
      {tier && (
        <p className={`text-xs font-semibold ${left !== null && max === 0 ? "text-red-600" : "text-[#1e3a5f]"}`} data-testid={`issue-capacity-left-${i}`}>
          {left === null ? "Unlimited capacity" : max === 0 ? "No seats left — cannot issue" : `Max you can issue now: ${max}`}
          {left !== null && <span className="font-normal opacity-80"> · {left} seat(s) left of {tier.capacity}{tier.held ? ` · ${tier.held} on hold` : ""}</span>}
        </p>
      )}
      {quoteLine && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className={line.override ? "line-through text-slate-400" : "text-slate-600"} data-testid={`issue-normal-price-${i}`}>Normal {eur(quoteLine.normal_unit_price)} / ticket</span>
          <label className="flex items-center gap-1 text-slate-600 cursor-pointer">
            <input type="checkbox" checked={line.override} onChange={e => onChange({ override: e.target.checked })} data-testid={`issue-override-toggle-${i}`} /> Override price
          </label>
          {line.override && <input type="number" min="0" step="0.01" value={line.unit_price} onChange={e => onChange({ unit_price: e.target.value })} placeholder="Price per ticket"
            className="w-32 px-2 py-1 rounded-lg border text-sm" data-testid={`issue-price-input-${i}`} />}
          <span className="ml-auto font-semibold text-[#1e3a5f]" data-testid={`issue-line-subtotal-${i}`}>{eur(quoteLine.subtotal)}</span>
        </div>
      )}
    </div>
  );
}

function QuoteTotals({ quote }) {
  if (!quote) return null;
  return (
    <div className="rounded-xl bg-slate-50 border p-3 text-sm space-y-0.5" data-testid="issue-price-panel">
      <p className="flex justify-between"><span>Tickets</span><span data-testid="issue-quote-total">{eur(quote.total)}</span></p>
      {quote.fee > 0 && <p className="flex justify-between text-slate-500"><span>Payment processing charges</span><span>{eur(quote.fee)}</span></p>}
      <p className="flex justify-between font-bold text-[#1e3a5f]"><span>Buyer pays</span><span data-testid="issue-quote-buyer-pays">{eur(quote.buyer_pays)}</span></p>
    </div>
  );
}

function IssueForm({ authAxios, tiers, onIssued }) {
  const [lines, setLines] = useState([newLine()]);
  const [hold, setHold] = useState(12);
  const [buyer, setBuyer] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(0);
  const lineOk = (l, i) => {
    const t = tiers.find(x => x.id === l.ticket_type_id);
    const q = Number(l.quantity);
    return t && (!t.variants?.length || l.variant_id) && q >= 1 && q <= capacityOf(t, lines, i).max;
  };
  const ready = buyer && lines.every(lineOk);
  const body = () => ({
    email: buyer.email, name: buyer.name,
    items: lines.map(l => ({ ticket_type_id: l.ticket_type_id, variant_id: l.variant_id || null, quantity: Number(l.quantity),
      unit_price: l.override && l.unit_price !== "" ? Number(l.unit_price) : null })),
  });
  const sig = JSON.stringify(lines.map(({ key, ...l }) => l));

  useEffect(() => {
    if (!ready) { setQuote(null); return; }
    const t = setTimeout(() => {
      authAxios.post("/shop-admin/ticket-links/quote", body()).then(r => setQuote(r.data)).catch(() => setQuote(null));
    }, 250);
    return () => clearTimeout(t);
  }, [ready, sig, buyer]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, patch) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const submit = async () => {
    if (!ready) return toast.error("Choose a buyer and ticket");
    setBusy(true);
    try {
      await authAxios.post("/shop-admin/ticket-links", { ...body(), hold_hours: hold, origin_url: window.location.origin });
      toast.success(`Payment link sent to ${buyer.email}`);
      setLines([newLine(lines[0].ticket_type_id)]); setBuyer(null); setRound(r => r + 1);
      onIssued();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not issue tickets"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3" data-testid="issue-tickets-form">
      <p className="text-sm text-slate-500">Works even when shop bookings are closed. Seats are held for the chosen time; tickets are issued only after the buyer pays via the emailed Stripe link.</p>
      <BuyerPicker key={round} authAxios={authAxios} buyer={buyer} onChange={setBuyer} />
      {lines.map((l, i) => (
        <LineEditor key={l.key} line={l} i={i} tiers={tiers} lines={lines} quoteLine={quote?.lines?.[i]}
          onChange={(p) => update(i, p)} onRemove={() => setLines(ls => ls.filter((_, j) => j !== i))} />
      ))}
      {lines.length < 10 && (
        <button onClick={() => setLines([...lines, newLine()])} className="text-sm font-semibold text-[#1e3a5f] flex items-center gap-1 hover:underline" data-testid="issue-add-line-btn">
          <Plus className="w-4 h-4" /> Add another ticket type
        </button>
      )}
      <QuoteTotals quote={quote} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">Hold seats & link valid for</span>
        {HOLDS.map(h => (
          <button key={h} type="button" onClick={() => setHold(h)} data-testid={`issue-hold-${h}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${hold === h ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600"}`}>{h}h</button>
        ))}
      </div>
      <button onClick={submit} disabled={busy || !ready} className="w-full sm:w-auto px-6 py-3 rounded-full bg-[#1e3a5f] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="issue-submit-btn">
        <Send className="w-4 h-4" /> {busy ? "Sending…" : quote ? `Hold seats & send link · ${eur(quote.buyer_pays)}` : "Hold seats & send payment link"}
      </button>
    </div>
  );
}

export function LinkRow({ l, onAction, showEvent }) {
  return (
    <tr className="border-t" data-testid={`ticket-link-row-${l.id}`}>
      <td className="p-3"><p className="font-semibold flex items-center gap-1.5">{l.buyer_name} <BuyerBadge type={l.buyer_role === "guest_otp" ? "unregistered" : l.buyer_role} /></p><p className="text-xs text-slate-500">{l.buyer_email}</p></td>
      <td className="p-3">{l.lines || `${l.quantity}× ${l.tier_name}${l.variant_name ? ` – ${l.variant_name}` : ""}`}{showEvent && <span className="block text-[10px] text-slate-400">{l.event_name}</span>}</td>
      <td className="p-3 text-right">{eur(l.total)}{l.price_overridden && <span className="block text-[10px] text-amber-600">custom price</span>}{l.stripe_fee > 0 && <span className="block text-[10px] text-slate-400">+{eur(l.stripe_fee)} processing</span>}</td>
      <td className="p-3 text-xs text-slate-500">{new Date(l.hold_until).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</td>
      <td className="p-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[l.status] || ""}`} data-testid={`ticket-link-status-${l.id}`}>{l.status.toUpperCase()}</span></td>
      <td className="p-3">
        {l.status === "pending" && (
          <div className="flex gap-1 justify-end">
            <button onClick={() => { navigator.clipboard?.writeText(l.checkout_url); toast.success("Link copied"); }} className="p-1.5 rounded-lg hover:bg-slate-100" title="Copy link" data-testid={`ticket-link-copy-${l.id}`}><Copy className="w-4 h-4" /></button>
            <button onClick={() => onAction(l, "resend")} className="p-1.5 rounded-lg hover:bg-slate-100" title="Re-send email" data-testid={`ticket-link-resend-${l.id}`}><RefreshCw className="w-4 h-4" /></button>
            <button onClick={() => onAction(l, "cancel")} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Cancel link & release seats" data-testid={`ticket-link-cancel-${l.id}`}><XCircle className="w-4 h-4" /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

export function useLinkActions(authAxios, reload) {
  return async (l, action) => {
    if (action === "cancel" && !window.confirm(`Cancel the payment link for ${l.buyer_email} and release ${l.quantity} seat(s)?`)) return;
    try {
      await authAxios.post(`/shop-admin/ticket-links/${l.id}/${action}`);
      toast.success(action === "cancel" ? "Link cancelled, seats released" : "Payment link re-sent");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
}

export function LinksTable({ links, onAction, showEvent, empty, testid }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-slate-50 text-xs text-slate-500 text-left">
          <tr><th className="p-3">Buyer</th><th className="p-3">Tickets</th><th className="p-3 text-right">Amount</th><th className="p-3">Hold until</th><th className="p-3">Status</th><th className="p-3" /></tr>
        </thead>
        <tbody>
          {links.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-slate-400" data-testid={`${testid}-empty`}>{empty}</td></tr>
            : links.map(l => <LinkRow key={l.id} l={l} onAction={onAction} showEvent={showEvent} />)}
        </tbody>
      </table>
    </div>
  );
}

export function PendingLinksPanel({ authAxios }) {
  const [links, setLinks] = useState([]);
  const load = () => authAxios.get("/shop-admin/ticket-links", { params: { status: "pending" } }).then(r => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onAction = useLinkActions(authAxios, load);
  return (
    <div className="mb-5" data-testid="pending-links-panel">
      <h3 className="font-bold text-sm text-[#1e3a5f] mb-2 flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-600" /> Awaiting payment
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="pending-links-count">{links.length}</span>
      </h3>
      <LinksTable links={links} onAction={onAction} showEvent empty="No tickets awaiting payment." testid="pending-links-table" />
    </div>
  );
}

export default function IssueTicketsTab({ authAxios }) {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [tiers, setTiers] = useState([]);
  const [links, setLinks] = useState([]);

  useEffect(() => { authAxios.get("/shop-admin/events").then(r => setEvents(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    if (!eventId) return;
    authAxios.get("/shop-admin/ticket-types", { params: { event_id: eventId } }).then(r => setTiers(r.data.filter(t => t.is_active)));
    authAxios.get("/shop-admin/ticket-links", { params: { event_id: eventId } }).then(r => setLinks(r.data));
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(load, [load]);

  const onAction = useLinkActions(authAxios, load);

  return (
    <div className="space-y-4" data-testid="issue-tickets-tab">
      <div className="flex items-center gap-2">
        <Link2 className="w-5 h-5 text-[#1e3a5f]" />
        <h2 className="font-bold text-lg text-[#1e3a5f]">Issue Tickets via Payment Link</h2>
      </div>
      <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="w-full sm:w-96 px-3 py-2.5 rounded-xl border text-sm bg-white" data-testid="issue-event-select">
        <option value="">Choose event…</option>
        {events.map(e => <option key={e.id} value={e.id}>{`${e.name} (${e.start_date})`}</option>)}
      </select>
      {eventId && <IssueForm key={eventId} authAxios={authAxios} tiers={tiers} onIssued={load} />}
      {eventId && <LinksTable links={links} onAction={onAction} empty="No payment links yet." testid="ticket-links-table" />}
    </div>
  );
}
